import { Box, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { IPaginationSettings, getPaginationSettings, savePaginationSettings } from 'wisestep-api';
import { Label, WsButton, WsSelect, WsTypography, WsTypoVariants } from 'wisestep-base';

import { SettingsPageLayout } from '../../../components/SettingsPageLayout';
import { useWorkspaceContext } from '../../../context';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../../hooks/useAtsCustomAPIAccess';
import { warning, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { paginationSettingsStore } from '../../../utils/PaginationSettingStore';
import { HeaderBox } from '../../jobSettings/components/HeaderBox';
import { defaultPaginationSettings } from '../CRMSettingsHelper';

const PaginationSettingsPage = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [paginationData, setPaginationData] =
    useState<IPaginationSettings>(defaultPaginationSettings);
  const setPaginationsSettings = paginationSettingsStore((state) => state.setPaginationSettings);
  const methods = useForm();
  const { handleSubmit } = methods;
  const options = [
    { name: 'Pipeline', fieldName: 'candidatePagination' },
    { name: 'Talent pool', fieldName: 'talentBenchPagination' },
    { name: 'Submitted Candidates', fieldName: 'submittedCandidatesPagination' },
    { name: 'Job CRM', fieldName: 'jobCrmPagination' },
    { name: 'Jobs List', fieldName: 'jobsListPagination' },
  ];
  const workspaceContext = useWorkspaceContext();
  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_PAGINATION_SETTINGS,
  ]);

  const manageAccess = workspaceContext.owns || atsCustomAccessControl[0];
  useEffect(() => {
    (async function () {
      const getPaginationData = await getPaginationSettings();
      if (getPaginationData?.data.id) setPaginationData(getPaginationData.data);
    })();
  }, []);
  const onSaveHandler = async (data: FieldValues) => {
    try {
      if (paginationData && data) {
        const tempData: IPaginationSettings = paginationData;
        tempData.candidatePagination =
          data.candidatePagination ?? paginationData.candidatePagination;
        tempData.talentBenchPagination =
          data.talentBenchPagination ?? paginationData.talentBenchPagination;
        tempData.submittedCandidatesPagination =
          data.submittedCandidatesPagination ?? paginationData.submittedCandidatesPagination;
        tempData.jobCrmPagination = data.jobCrmPagination ?? paginationData.jobCrmPagination;
        tempData.jobsListPagination = data.jobsListPagination ?? paginationData.jobsListPagination;
        await savePaginationSettings({ ...tempData });
        localStorage.removeItem('paginationSettings');
        setPaginationsSettings();
        setNotification(success('Pagination Settings updated successfully'));
      }
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };
  return (
    <SettingsPageLayout
      title={'Pagination Settings'}
      indexOptions={[{ label: 'Pagination Settings', url: '/pagination-settings' }]}
      breadCrumnbsList={getSettingBreadCrumbList(
        ISettingBreadCrumbTypes.CRM,
        Pages['PAGINATION_SETTINGS']
      )}
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          px={8}
          pt={2}
          display="flex"
          flexDirection="column"
          onSubmit={handleSubmit(onSaveHandler)}
        >
          <HeaderBox mb={4}>
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>Pagination Settings</WsTypography>
            {manageAccess && (
              <WsButton type="submit" size="small">
                Save
              </WsButton>
            )}
          </HeaderBox>

          {options.map((option, index) => (
            <Grid container mb={2} justifyContent="space-between" alignItems="center" key={index}>
              <Grid item sm={7}>
                <Label sx={{ fontWeight: 'bold', ml: 2 }}>{option.name}</Label>
              </Grid>
              <Grid item sm={5}>
                <WsSelect
                  name={option.fieldName}
                  key={option.fieldName}
                  label="Records"
                  defaultValue={paginationData[option.fieldName]}
                  selectArray={[
                    { label: '20', value: '20' },
                    { label: '50', value: '50' },
                  ]}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            </Grid>
          ))}
        </Box>
      </FormProvider>
    </SettingsPageLayout>
  );
};

export default PaginationSettingsPage;
